export function assertDefined(value) {
    if (value === undefined || value === null) {
        throw new Error(`value ${value} isn't defined`);
    }
}
export function ensureDefined(value) {
    assertDefined(value);
    return value;
}
/**
 * call callback only if value is defined
 */
export function ifDefined(input, fn) {
    if (input === null || input === undefined) {
        return input;
    }
    return fn(input);
}
// istanbul ignore next
export function deepFreeze(obj) {
    const propNames = Object.getOwnPropertyNames(obj);
    for (const name of propNames) {
        // @ts-expect-error
        const value = obj[name];
        if (value && typeof value === 'object') {
            deepFreeze(value);
        }
    }
    return Object.freeze(obj);
}
export function deepTransform(input, transformer, path = '') {
    if (input === null
        || typeof input !== 'object'
        || input instanceof Date
        || input instanceof RegExp) {
        return transformer(input, path);
    }
    if (Array.isArray(input)) {
        return input.map((item, index) => deepTransform(item, transformer, `${path}[${index}]`));
    }
    const result = {};
    for (const [key, value] of Object.entries(input)) {
        const childPath = path ? `${path}.${key}` : key;
        result[key] = deepTransform(value, transformer, childPath);
    }
    return result;
}
/**
 * makes easier to extract prefixed fields from an object.
 * Respects camelCase
 */
export function extractPrefix(obj, prefix) {
    const result = {};
    for (const key in obj) {
        if (key.startsWith(prefix)) {
            const stripped = key.slice(prefix.length);
            const newKey = stripped.charAt(0).toLowerCase() + stripped.slice(1);
            result[newKey] = obj[key];
        }
    }
    return result;
}
/**
 * this is a type-safe version of `key in obj` check
 */
export function hasKey(obj, key) {
    return typeof obj === 'object' && obj !== null && key in obj;
}
/**
 * TypeGuard for checking if object has a non-null/-undefined property
 */
export function hasProp(key) {
    return (obj) => obj[key] !== null && obj[key] !== undefined;
}
export function pickArray(obj, keys) {
    return keys.map(key => obj[key]);
}
/**
 * returns list of keys which values differ between obj1 and obj2
 */
export function diffObject(obj1, obj2) {
    const changed = [];
    for (const key in obj1) {
        // istanbul ignore else
        if (Object.prototype.hasOwnProperty.call(obj1, key)) {
            const val1 = obj1[key];
            const val2 = obj2[key];
            if (!isEqualDeep(val1, val2)) {
                changed.push(key);
            }
        }
    }
    // Also check keys present in obj2 but not in obj1
    for (const key in obj2) {
        // istanbul ignore else
        if (Object.prototype.hasOwnProperty.call(obj2, key)) {
            if (!(key in obj1)) {
                changed.push(key);
            }
        }
    }
    return changed;
}
/**
 * deep equality check for objects and arrays, order-independent
 */
function isEqualDeep(a, b) {
    // primitive types or equal by reference
    if (a === b) {
        return true;
    }
    if (a === null || b === null || a === undefined || b === undefined) {
        return false;
    }
    // Arrays (order-independent)
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            return false;
        }
        const used = new Array(b.length).fill(false);
        for (const itemA of a) {
            let found = false;
            for (let i = 0; i < b.length; i++) {
                if (used[i]) {
                    continue;
                }
                if (isEqualDeep(itemA, b[i])) {
                    used[i] = true;
                    found = true;
                    break;
                }
            }
            if (!found) {
                return false;
            }
        }
        return true;
    }
    // Date handling
    if (a instanceof Date || b instanceof Date) {
        // istanbul ignore else
        if (a instanceof Date && b instanceof Date) {
            return a.getTime() === b.getTime();
        }
        // istanbul ignore next
        return false;
    }
    if (typeof a === 'object' && typeof b === 'object') {
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        if (keysA.length !== keysB.length) {
            return false;
        }
        for (const key of keysA) {
            if (!isEqualDeep(a[key], b[key])) {
                return false;
            }
        }
        return true;
    }
    return false;
}
//# sourceMappingURL=ObjectUtils.js.map